/**
 * 校徽样式默认图层与画布常量（设计画布 1172×435，与案例图/空白底图一致）。最后更新：2026-06-09
 */

/** 默认空白底图（带版本号防缓存） */
export const defaultBadgeBackgroundUrl = '/badge-order/default-badge-bg.png?v=10'
/** 同 defaultBadgeBackgroundUrl，兼容旧引用 */
export const defaultBadgeBackgroundPngUrl = defaultBadgeBackgroundUrl
/** 产品详情案例图路径 */
export const productDetailSampleImagePath = '/badge-order/product-sample.png'
/** 预览用示例证件照 */
export const previewSamplePhotoUrl = '/badge-order/preview-sample-photo.png?v=7'

/** 设计画布宽度（与 product-sample.png / default-badge-bg.png 一致） */
export const badgeCanvasWidth = 1172
/** 设计画布高度 */
export const badgeCanvasHeight = 435

/** 图层元数据（类型、键名、中文标签） */
export const badgeLayerMetas = [
  { type: 'image', key: 'photo', label: '学生照片' },
  { type: 'text', key: 'schoolName', label: '学校名称' },
  { type: 'text', key: 'className', label: '班级' },
  { type: 'text', key: 'studentName', label: '学生姓名' },
  { type: 'text', key: 'badgeNo', label: '校徽编号' },
  { type: 'qrcode', key: 'moralQr', label: '德育二维码' }
]

/** 与 product-sample 案例图实测坐标一致 */
const defaultLayersArray = [
  { type: 'image', key: 'photo', x: 127, y: 103, w: 201, h: 263, borderRadius: 0 },
  { type: 'text', key: 'schoolName', x: 370, y: 15, fontSize: 40, fontWeight: 'bold', color: '#ffffff', hidden: true },
  { type: 'text', key: 'className', x: 558, y: 121, fontSize: 40, fontWeight: 'bold', color: '#000000' },
  { type: 'text', key: 'studentName', x: 558, y: 199, fontSize: 40, fontWeight: 'bold', color: '#000000' },
  { type: 'text', key: 'badgeNo', x: 558, y: 277, fontSize: 40, fontWeight: 'bold', color: '#000000' },
  { type: 'qrcode', key: 'moralQr', x: 856, y: 87, size: 238 }
]
/**
 * 将德育二维码底边与头像区域底边对齐
 * @param {Object} layerMap - 图层键值映射
 * @returns {Object}
 */
export function alignPhotoQrBottom(layerMap) {
  if (!layerMap || !layerMap.photo || !layerMap.moralQr) return layerMap
  const photo = layerMap.photo
  const qr = layerMap.moralQr
  const photoBottom = (photo.y || 0) + (photo.h || 96)
  qr.size = qr.size || 96
  qr.y = photoBottom - qr.size
  if (qr.y < 0) qr.y = 0
  return layerMap
}

/** 样式预览默认模拟数据（姓名、编号、示例照） */
export const templatePreviewMockDefault = {
  schoolName: '厦门第一实验小学',
  className: '五年级1班',
  studentName: '欧阳静静',
  badgeNo: '20230208',
  photoUrl: previewSamplePhotoUrl,
  moralQrUrl: ''
}

/**
 * 预览时生成德育二维码图片 URL（无 moralQrUrl 时使用）
 * @param {string} badgeNo - 校徽编号
 * @param {number} [size] - 边长像素
 * @returns {string}
 */
export function buildPreviewQrUrl(badgeNo, size) {
  const no = (badgeNo || '').trim()
  if (!no) return ''
  const s = size || 120
  return (
    'https://api.qrserver.com/v1/create-qr-code/?size=' +
    s +
    'x' +
    s +
    '&data=' +
    encodeURIComponent(no)
  )
}

/**
 * 将图层 JSON 解析为键值映射，缺失项用默认坐标补全
 * @param {string} json - LayersJson 字符串
 * @returns {Object}
 */
export function parseBadgeLayers(json) {
  let arr = []
  try {
    arr = JSON.parse(json || '[]')
  } catch (e) {
    arr = []
  }
  const map = {}
  badgeLayerMetas.forEach(meta => {
    const found = arr.find(l => l && l.key === meta.key)
    const fallback = defaultLayersArray.find(l => l.key === meta.key) || { type: meta.type, key: meta.key }
    map[meta.key] = Object.assign({}, fallback, found || {})
    map[meta.key].type = meta.type
    map[meta.key].key = meta.key
  })
  return map
}

/** 获取默认图层映射（未保存模板时使用） */
export function getDefaultBadgeLayerMap() {
  return parseBadgeLayers(JSON.stringify(defaultLayersArray))
}

/**
 * 将旧版底图 URL 统一为带版本号的 default-badge-bg
 * @param {string} url
 * @returns {string}
 */
export function normalizeDefaultBadgeBackgroundUrl(url) {
  if (!url) return url
  const raw = url.split('?')[0]
  if (raw === '/badge-order/default-badge-bg.png' || raw.endsWith('/default-badge-bg.png')) {
    return defaultBadgeBackgroundUrl
  }
  return url
}

/**
 * 判断数据库中的模板是否需要迁移到新画布/坐标/底图版本
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @param {string} layersJson
 * @param {string} previewImageUrl
 * @returns {boolean}
 */
export function needsBadgeLayerMigration(canvasWidth, canvasHeight, layersJson, previewImageUrl) {
  const bg = (previewImageUrl || '').split('?')[0]
  if (bg.indexOf('default-badge-bg') < 0 && bg.indexOf('badge-order') >= 0) return true
  if ((previewImageUrl || '').indexOf('v=10') < 0 && bg.indexOf('default-badge-bg') >= 0) return true
  if (canvasWidth !== badgeCanvasWidth || canvasHeight !== badgeCanvasHeight) return true
  try {
    const arr = JSON.parse(layersJson || '[]')
    const photo = arr.find(l => l && l.key === 'photo')
    const name = arr.find(l => l && l.key === 'studentName')
    const qr = arr.find(l => l && l.key === 'moralQr')
    if (!photo || (photo.w || 0) !== 201 || (photo.h || 0) !== 263) return true
    if (!name || (name.x || 0) !== 558 || (name.y || 0) !== 199) return true
    if (!qr || (qr.x || 0) !== 856 || (qr.size || 0) !== 238) return true
    return false
  } catch (e) {
    return true
  }
}

/**
 * 将图层映射序列化为存储用 JSON 字符串
 * @param {Object} layerMap
 * @returns {string}
 */
export function stringifyBadgeLayers(layerMap) {
  const list = badgeLayerMetas.map(meta => {
    const layer = layerMap[meta.key]
    if (!layer) return null
    const item = { type: meta.type, key: meta.key, x: layer.x || 0, y: layer.y || 0 }
    if (layer.hidden) item.hidden = true
    if (meta.type === 'image') {
      item.w = layer.w || 72
      item.h = layer.h || 96
      item.borderRadius = layer.borderRadius || 0
    } else if (meta.type === 'text') {
      item.fontSize = layer.fontSize || 14
      item.color = layer.color || '#303133'
      if (layer.fontWeight && layer.fontWeight !== 'normal') item.fontWeight = layer.fontWeight
    } else if (meta.type === 'qrcode') {
      item.size = layer.size || 80
    }
    return item
  }).filter(Boolean)
  return JSON.stringify(list)
}

/** 默认图层 JSON（新建模板初始值） */
export const defaultBadgeLayersJson = stringifyBadgeLayers(getDefaultBadgeLayerMap())
